import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Dayjs } from "@utils/dayjs";
import useLocale from "@utils/useLocale";
import { ProductType } from "../types";
import { convertToMonthlyPriceStr, convertToPriceStr } from "@utils/money";
import { convertToPercentStr } from "@utils/percentages";

interface Props {
  title: string;
  description: string;
  validFrom: Dayjs | null;
  validDurationMinutes: number | null;
  unitPrice: number;
  productType: ProductType;
  vatPercentage: number;
  children?: React.ReactNode;
}

function ParkingPermitSummary(props: Props) {
  const { locale } = useLocale();
  const { t } = useTranslation();

  const validFrom = props.validFrom
    ? props.validFrom.format("LLLL")
    : t("purchaseParkingPermit.validFromPurchase");

  const day = t("purchaseParkingPermit.dayAbrv");

  const validTo = props.validDurationMinutes
    ? props.validFrom?.add(props.validDurationMinutes, "minutes").format("LLLL")
    : null;
  const isMonthly = props.productType === "MONTHLY";

  const unitPriceText = isMonthly
    ? convertToMonthlyPriceStr(props.unitPrice, locale, day)
    : convertToPriceStr(props.unitPrice, locale);

  return (
    <Stack sx={{ backgroundColor: "white" }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        padding={2}
        border={1}
        borderColor="divider"
        borderRadius="4px 4px 0 0"
      >
        <Typography variant="h6">{props.title}</Typography>
        <Stack textAlign="right">
          <Typography variant="h6">{unitPriceText}</Typography>
          <Typography variant="caption">
            <i>
              {t("purchaseParkingPermit.includesVat")}{" "}
              {convertToPriceStr(props.unitPrice * props.vatPercentage, locale)}{" "}
              {convertToPercentStr(props.vatPercentage, locale)}
            </i>
          </Typography>
        </Stack>
      </Stack>
      <Stack padding={2} spacing={4} border={1} borderColor="divider">
        <Stack spacing={2}>
          <Typography variant="body1">{props.description}</Typography>
          <ProductTypeInfo productType={props.productType} />
          <Typography variant="body1">
            {t("purchaseParkingPermit.validFrom")} <br />
            <b>{validFrom}</b>
          </Typography>
          {validTo && (
            <Typography variant="body1">
              {t("purchaseParkingPermit.validTo")} <br />
              <b>{validTo}</b>
            </Typography>
          )}
          {!validTo && props.validDurationMinutes && (
            <Typography variant="body1">
              {t("purchaseParkingPermit.validDuration")} <br />
              <b>
                {t("purchaseParkingPermit.validDurationDays", {
                  days: Math.floor(props.validDurationMinutes / (60 * 24)),
                })}
              </b>
            </Typography>
          )}
          {isMonthly && (
            <Typography data-testid="monthly-payment" variant="body1">
              {t("purchaseParkingPermit.recurrentBilling")}:<br />
              <b>{unitPriceText}</b>
            </Typography>
          )}
        </Stack>
        {props.children}
      </Stack>
    </Stack>
  );
}

const ProductTypeInfo = ({ productType }: { productType: ProductType }) => {
  const { t } = useTranslation();
  if (productType === "MONTHLY") {
    return (
      <Typography variant="body1">
        <b>{t("purchaseParkingPermit.recurrentBillingInfo")}</b>
      </Typography>
    );
  }
  if (productType === "SINGLE_PAYMENT") {
    return (
      <Typography variant="body1">
        <b>{t("purchaseParkingPermit.singlePaymentInfo")}</b>
      </Typography>
    );
  }

  return null;
};
export default ParkingPermitSummary;
